import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMerchant, useUser } from "@auth";
import {
  getPortalNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "@api";
import { invalidateNotifications } from "@api/queryKeys";
import { channels } from "../../realtime/channels";
import { useCoalescedInvalidation } from "../../realtime/useCoalescedInvalidation";

const PAGE_SIZE = 20;

// Maps { previousFeed, previousCount } → optimistic infinite-query cache updates shared by both
// mark-read mutations below.
const setAllRead = (data) =>
  data && {
    ...data,
    pages: data.pages.map((page) => page.map((n) => ({ ...n, status: "Read" }))),
  };

const setOneRead = (data, id) =>
  data && {
    ...data,
    pages: data.pages.map((page) => page.map((n) => (n.id === id ? { ...n, status: "Read" } : n))),
  };

const feedKey = ["notifications", "feed"];
const countKey = ["notifications", "unreadCount"];

/**
 * Marks a single notification read, optimistically flipping it in the feed cache and decrementing the
 * unread count. Split out so the toaster can mark a clicked toast read without also mounting the feed
 * query and realtime subscription.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: feedKey });
      const previousFeed = queryClient.getQueryData(feedKey);
      const previousCount = queryClient.getQueryData(countKey);
      const wasUnread = previousFeed?.pages
        ?.flat()
        .find((n) => n.id === id)?.status === "Unread";

      queryClient.setQueryData(feedKey, (data) => setOneRead(data, id));
      if (wasUnread) {
        queryClient.setQueryData(countKey, (count) => Math.max(0, (count ?? 0) - 1));
      }

      return { previousFeed, previousCount };
    },
    onError: (_err, _id, context) => {
      if (context) {
        queryClient.setQueryData(feedKey, context.previousFeed);
        queryClient.setQueryData(countKey, context.previousCount);
      }
    },
    onSettled: () => invalidateNotifications(queryClient),
  });

  return mutation.mutate;
}

/**
 * Backs the bell FAB: the notification feed (paged, loaded 20 at a time via infinite query) + the
 * unread count, mark-read / mark-all-read mutations, and a realtime subscription that refetches
 * both on a "new notification" hint. Hints are coalesced (useCoalescedInvalidation): a bulk event
 * that creates hundreds of notifications for one user costs one refetch per window, not one per
 * notification.
 */
export function usePortalNotifications() {
  const { merchant } = useMerchant();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const feedQuery = useInfiniteQuery({
    queryKey: feedKey,
    queryFn: ({ pageParam, signal }) =>
      getPortalNotifications({ skip: pageParam, take: PAGE_SIZE, signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      (lastPage?.length ?? 0) < PAGE_SIZE ? undefined : allPages.length * PAGE_SIZE,
    staleTime: 30_000,
  });

  const unreadCountQuery = useQuery({
    queryKey: countKey,
    queryFn: ({ signal }) => getUnreadCount({ signal }),
    staleTime: 30_000,
  });

  const markRead = useMarkNotificationRead();

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: feedKey });
      const previousFeed = queryClient.getQueryData(feedKey);
      const previousCount = queryClient.getQueryData(countKey);

      queryClient.setQueryData(feedKey, setAllRead);
      queryClient.setQueryData(countKey, 0);

      return { previousFeed, previousCount };
    },
    onError: (_err, _vars, context) => {
      if (context) {
        queryClient.setQueryData(feedKey, context.previousFeed);
        queryClient.setQueryData(countKey, context.previousCount);
      }
    },
    onSettled: () => invalidateNotifications(queryClient),
  });

  const channelPath = merchant?.id && user?.guid ? channels.user(merchant.id, user.guid) : null;
  // Invalidating the ["notifications"] prefix covers the feed, the unread count and the toaster's
  // recent list in one pass.
  useCoalescedInvalidation(channelPath, ["notifications"]);

  return {
    notifications: feedQuery.data?.pages.flat() ?? [],
    isLoading: feedQuery.isLoading,
    isError: feedQuery.isError,
    fetchNextPage: feedQuery.fetchNextPage,
    hasNextPage: !!feedQuery.hasNextPage,
    isFetchingNextPage: feedQuery.isFetchingNextPage,
    unreadCount: unreadCountQuery.data ?? 0,
    markRead,
    markAllRead: markAllReadMutation.mutate,
    isMarkingAllRead: markAllReadMutation.isPending,
  };
}

export default usePortalNotifications;
